import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import { Material } from "../../../data/entities";
import BackButton from "../../../shared/components/BackButton";
import CancelModal from "../../../shared/components/CancelModal";
import { FieldConfigs } from "../../../shared/components/FormType";
import PageLoader from "../../../shared/components/LoadingPage";
import { api } from "../../../shared/hooks/api";
import { error, success } from "../../../utils/toast";
import Title from "../create/Title";
import { SingleMaterial } from "../single";
import MaterialExpirationManager from "../single/MaterialExpirationManager";
import ProviderLinkedList from "../single/MaterialList";
import TransactionHistory from "../single/Transaction History";
import { ConversionRateField } from "./ConversionRateField";
import MaterialOutsourceStandardTab from "./OutsourceStandard";
import PriceStatistics from "./PriceStatistics";
import { ContentWrapper } from "./Styled";
import DetailMaterialTabNavigation from "./TabBar";

const processMaterial = (material: Material) => {
    let providers = [];
    if (material) {
        providers = material.providers.map((item: any) => {
            if (item.provider) {
                return {
                    ...item.provider,
                    price: item.price,
                    note: item.note ?? item.provider.note,
                    category_group_id: item.category_group?.id || null,
                };
            }
            return item;
        });
    }
    return {
        ...material,
        providers: providers,
    };
};

const processUpdateMaterial = (material: Material) => {
    const providers = material.providers.map((item: any) => ({
        provider_id: item.id,
        price: item.price,
        note: item.note,
    }));
    return {
        ...material,
        providers: providers,
        category_group_id: material.category_group?.id || null,
    };
};

const MaterialDetail = () => {
    const { materialId } = useParams();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [{ data, isLoading, setLocalData, setLocalUrl: setGetUrl }, fetchMaterial] = api.get(
        `/materials/${materialId}/`
    );

    const material = useMemo(() => processMaterial(data), [data]);

    const [{ isUpdating, setLocalUrl: setUpdateUrl }, updateMaterial] = api.patch(
        `/materials/${materialId}/`
    );
    const [{ isDeleting }, deleteMaterial] = api.delete(`/materials/${materialId}/`);
    const [{ data: logisticManagerData }] = api.get(
        `/users/?role=LOGISTIC_MANAGER`
    );

    const materialDetailFields: FieldConfigs[] = [
        {
            label: "Trạng thái vật tư",
            type: "toggle",
            key: "is_active",
            required: true,
        },
        { label: "Mã vật tư", type: "text", key: "material_code", required: true },
        { label: "Tên vật tư", type: "text", key: "name", required: true },
        {
            label: "Mức cảnh báo tồn kho",
            type: "text",
            key: "threshold",
            required: false,
            validation: "number",
        },
        {
            label: "Nhóm vật tư",
            type: "quickFills",
            key: "category_group",
            required: true,
            dataApi: "/materials/category-groups/",
            placeholder: "Chọn nhóm vật tư",
        },
        {
            label: "Kho số",
            type: "dropdown",
            key: "storage",
            required: true,
            options: [
                { text: "Kho 1", value: "Kho 1" },
                { text: "Kho 2", value: "Kho 2" },
            ],
        },
        {
            label: "Hạng mục",
            type: "dropdown",
            key: "category",
            required: true,
            options: [
                { text: "Vật tư chính", value: "Vật tư chính" },
                { text: "Vật tư phụ", value: "Vật tư phụ" },
                { text: "Vật tư khác", value: "Vật tư khác" },
            ],
        },
        {
            label: "Đơn vị chính",
            type: "dropdown",
            key: "measurement_unit",
            required: false,
            options: [
                { text: "mét", value: "mét" },
                { text: "kg (kilogram)", value: "kg" },
                { text: "lít", value: "lít" },
                { text: "thùng", value: "thùng" },
                { text: "cái", value: "cái" },
                { text: "tấm", value: "tấm" },
                { text: "chai", value: "chai" },
                { text: "bộ", value: "bộ" },
                { text: "bịch", value: "bịch" },
                { text: "cuộn", value: "cuộn" },
                { text: "cây", value: "cây" },
                { text: "con", value: "con" },
                { text: "miếng", value: "miếng" },
                { text: "tờ", value: "tờ" },
                { text: "tuýt", value: "tuýt" },
                { text: "hộp", value: "hộp" },
            ],
        },
        {
            label: "Đơn vị phụ",
            type: "dropdown",
            key: "secondary_measurement_unit",
            required: false,
            options: [
                { text: "mét", value: "mét" },
                { text: "kg (kilogram)", value: "kg" },
                { text: "lít", value: "lít" },
                { text: "thùng", value: "thùng" },
                { text: "cái", value: "cái" },
                { text: "tấm", value: "tấm" },
                { text: "chai", value: "chai" },
                { text: "bộ", value: "bộ" },
                { text: "bịch", value: "bịch" },
                { text: "cuộn", value: "cuộn" },
                { text: "cây", value: "cây" },
                { text: "con", value: "con" },
                { text: "miếng", value: "miếng" },
                { text: "tờ", value: "tờ" },
                { text: "tuýt", value: "tuýt" },
                { text: "hộp", value: "hộp" },
            ],
        },
        // {
        //     label: "Ngày hết hạn",
        //     type: "calendar",
        //     key: "expired_date",
        //     required: false,
        // },
        {
            label: "Số lượng",
            type: "text",
            key: "quantity",
            required: false,
            disabled: true,
        },
        { label: "Ghi chú (Boxes)", type: "text", key: "note", required: false },
    ];

    useEffect(() => {
        setGetUrl(`/materials/${materialId}/`);
        setUpdateUrl(`/materials/${materialId}/`);
    }, [materialId, setUpdateUrl, setGetUrl]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const handleSubmitMaterial = async () => {
        setValidated(true);
        setSubmitted(true);
        try {
            const processedData = processUpdateMaterial(material);
            await updateMaterial(processedData);
            await fetchMaterial();
            setValidated(false);
            setIsModified(false);
            success("THÀNH CÔNG", "Cập nhật vật tư thành công");
            return true;
        } catch (err) {
            error("Cập nhật vật tư thất bại", err);
            return false;
        }
    };

    const handleDeleteMaterial = async () => {
        try {
            await deleteMaterial();
            success("THÀNH CÔNG", "Xóa vật tư thành công");
            navigate("/materials");
        } catch (err) {
            error("Xóa vật tư thất bại", err);
        }
    };

    const handleChangeMaterialInfo = (updatedData: any) => {
        setIsModified(true);
        setLocalData({
            ...material,
            ...updatedData,
        });
    };

    const confirmNavigation = useCallback(
        (path: string) => {
            if (isModified) {
                setNavigationPath(path);
                setShowUnsavedDialog(true);
                return false;
            }

            navigate(path);
            return true;
        },
        [isModified, navigate]
    );

    const handleBackClick = () => {
        confirmNavigation("/materials");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        try {
            setValidated(true);
            const processedData = processUpdateMaterial(material);
            await updateMaterial(processedData);
            success("THÀNH CÔNG", "Cập nhật vật tư thành công");
            navigate(navigationPath);
        } catch (err) {
            error("Cập nhật vật tư thất bại", err);
        }
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const markAsModified = () => {
        setIsModified(true);
    };

    return (
        <ContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            {isLoading && <PageLoader />}
            {!isLoading && (
                <Title
                    title={material.name}
                    data={material}
                    setValidated={setValidated}
                    submitForm={handleSubmitMaterial}
                    onDataChange={markAsModified}
                    hasUnsavedChanges={isModified}
                    onCancelClick={() => confirmNavigation("/materials")}
                    isLoading={isUpdating}
                    deleteMaterial={handleDeleteMaterial}
                    isDeleting={isDeleting}
                />
            )}
            {!isLoading && (
                <DetailMaterialTabNavigation
                    materialDetailTab={
                        <SingleMaterial
                            material={material}
                            mergeMaterial={handleChangeMaterialInfo}
                            setSubmit={setSubmitted}
                            validated={validated}
                            materialFields={[
                                ...materialDetailFields.slice(0, -2),
                                {
                                    label: "Tỉ lệ chuyển đổi",
                                    type: "custom",
                                    key: "conversion_rate",
                                    required: false,
                                    customComponent: <ConversionRateField material={material} />,
                                },
                                ...materialDetailFields.slice(-2),
                            ]}
                        />
                    }
                    providerDetailTab={
                        <ProviderLinkedList
                            materialData={material}
                            mergeMaterial={handleChangeMaterialInfo}
                        />
                    }
                    outsourceStandardTab={<MaterialOutsourceStandardTab material={material} />}
                    transactionHistoryTab={<TransactionHistory materialId={material.id} />}
                    expirationManagementTab={
                        <MaterialExpirationManager
                            material={material}
                            fetchMaterial={fetchMaterial}
                        />
                    }
                    priceStatisticsTab={<PriceStatistics materialId={material.id} />}
                />
            )}

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </ContentWrapper>
    );
};

export default MaterialDetail;
